/**
 * Domain sparkline — pure data shaping (DC-1658, v2.5 Layer 3, surface c).
 *
 * Collapses one domain's compact composite readings (from the batch endpoint
 * GET /api/triangulation/domains/sparklines) into the values + text-equivalent
 * the readiness-dashboard sparkline renders: the trend line, the current value,
 * the since-baseline delta, and the low-coverage / rubric-change flags.
 *
 * The delta chip + current value ARE the sparkline's text equivalent (spec §4),
 * so the non-visual reader gets direction + magnitude without the chart. Scores
 * are stored [0,5]; scaled ×20 to the 0–100 display axis here.
 *
 * Honesty: a rubric change inside the window is flagged (never silently implying
 * "got worse" when the scorer changed — DC-1652), and low coverage is surfaced
 * rather than hidden.
 */

import { LOW_COVERAGE_DATASETS } from './domainTrajectoryData';

export interface DomainSparklinePointDTO {
  domain_id: string;
  overall_composite: number | null;
  scoring_version: string;
  dataset_count: number;
  captured_at: string;
}

export interface DomainSparklineModel {
  /** ≥2 readings — enough to draw a line. */
  hasTrend: boolean;
  /** composite ×20, oldest-first; nulls preserved (ApexCharts gaps them). */
  values: Array<number | null>;
  /** latest present composite on the 0–100 axis, or null. */
  latest: number | null;
  /** latest − earliest present composite, or null when either endpoint is null. */
  delta: number | null;
  /** delta < 0 — the end-dot turns terracotta (paired with the ▼ glyph). */
  negative: boolean;
  /** latest reading's dataset_count is below the coverage floor. */
  lowCoverage: boolean;
  /** scoring_version changed across the window — flagged so a drop isn't misread. */
  rubricChanged: boolean;
}

function to100(value: number | null): number | null {
  return value === null ? null : Math.round(value * 20);
}

function ms(iso: string): number {
  return new Date(iso).getTime();
}

/** Build the sparkline model for one domain's readings (any order). */
export function buildDomainSparklineModel(points: DomainSparklinePointDTO[]): DomainSparklineModel {
  const rows = [...points].sort((a, b) => ms(a.captured_at) - ms(b.captured_at));

  const values = rows.map((r) => to100(r.overall_composite));
  const present = values.filter((v): v is number => v !== null);
  const latest = present.length > 0 ? present[present.length - 1] : null;
  const baseline = present.length > 0 ? present[0] : null;
  const delta = latest !== null && baseline !== null ? latest - baseline : null;

  let rubricChanged = false;
  for (let k = 1; k < rows.length; k += 1) {
    if (rows[k].scoring_version !== rows[k - 1].scoring_version) {
      rubricChanged = true;
      break;
    }
  }

  return {
    hasTrend: rows.length >= 2,
    values,
    latest,
    delta,
    negative: delta !== null && delta < 0,
    lowCoverage: rows.length > 0 && rows[rows.length - 1].dataset_count < LOW_COVERAGE_DATASETS,
    rubricChanged,
  };
}
